import {
  CostItem,
  Row,
  Subtotal,
  TotalItem,
  Wrapper,
  DisplayValue,
  HelpIcon,
} from './OrderCost.styled';
import type {
  OrderCost as OrderCostType,
  OrderDeliveryType,
} from '../../../types/orders';

import { useTranslation } from 'next-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from 'react-tooltip';

interface Props {
  orderCost: OrderCostType | null;
  isLarge: boolean;
  customTotal?: boolean;
  customTip?: number;
  customTax?: number;
  orderType?: OrderDeliveryType;
  walletAmmount?: number;
  useWallet?: boolean;
  deliveryCostError?: boolean;
}

export const OrderCost = (
  props: Props = {
    orderCost: null,
    isLarge: false,
    customTip: 0,
    customTax: 0,
    customTotal: false,
    walletAmmount: 0,
    useWallet: false,
  }
) => {
  const { t } = useTranslation();
  const { orderCost, customTip, customTax, orderType } = props;

  const isCustomTotal = props.customTotal;
  const isUseWallet = props.useWallet;
  const isDiscount = orderCost?.discount;
  const walletBalance = props.orderCost?.walletBalance || 0;

  if (!orderCost) {
    return null;
  }

  const calculateWallet = () => {
    if (!props.walletAmmount) {
      return 0;
    }

    if (orderCost.productsCost > props.walletAmmount) {
      return props.walletAmmount;
    }

    return orderCost.productsCost - (isDiscount || 0);
  };

  let shippingCost = orderType === 'pickup' ? 0 : orderCost.deliveryCost;

  const hasCustomValues =
    isCustomTotal && customTax != null && customTip != null;

  let total = orderCost.total;

  if (isCustomTotal && hasCustomValues) {
    const customTotalSum =
      orderCost.productsCost + orderCost.serviceFee + customTax + customTip;
    total =
      customTotalSum + (orderType === 'pickup' ? -shippingCost : shippingCost);

    if (isUseWallet) {
      total -= calculateWallet();
    }

    if (isDiscount) {
      total -= isDiscount;
    }
  }

  const calculatedWallet = isCustomTotal && isUseWallet ? calculateWallet() : 0;
  const displayValue = isCustomTotal ? calculatedWallet : walletBalance;

  return (
    <Wrapper isLarge={props.isLarge}>
      <Row>
        <Subtotal>{t('cart.shared.subtotal')}</Subtotal>
        <Subtotal>
          {orderCost?.currency.symbol}
          {orderCost?.productsCost.toFixed(2)}
        </Subtotal>
      </Row>
      <Row>
        <CostItem>{t('cart.shared.discount')}</CostItem>
        <CostItem>
          {orderCost?.discount === 0
            ? `${orderCost?.currency.symbol}0`
            : `-${orderCost?.currency.symbol}${orderCost?.discount.toFixed(2)}`}
        </CostItem>
      </Row>
      <Row>
        <CostItem>{t('cart.shared.wallet')}</CostItem>
        <CostItem>
          <DisplayValue
            displayStatus={displayValue != 0 && (isUseWallet || !isCustomTotal)}
          >
            {(isUseWallet || !isCustomTotal) && displayValue != null
              ? displayValue === 0
                ? `${orderCost?.currency.symbol}0`
                : displayValue < 0
                  ? `-${orderCost?.currency.symbol}${Math.abs(
                    displayValue
                  ).toFixed(2)}`
                  : `-${orderCost?.currency.symbol}${displayValue.toFixed(2)}`
              : `${orderCost?.currency.symbol}0`}
          </DisplayValue>
        </CostItem>
      </Row>
      <Row>
        <CostItem>
          {t('cart.shared.service')}
          <HelpIcon
            data-tooltip-id="serviceFeeDetails"
            data-tooltip-content={t('cart.shared.service.tooltip.disclaimertext')}
          >
            <FontAwesomeIcon icon={faQuestionCircle} />
          </HelpIcon>
        </CostItem>
        <CostItem>
          {orderCost?.currency.symbol}
          {orderCost?.serviceFee}
        </CostItem>
      </Row>
      <Tooltip
        id="serviceFeeDetails"
        place="top"
        variant="dark"
        openOnClick
        globalCloseEvents={{ clickOutsideAnchor: true }}
        className="extraClass"
      />
      <Row>
        <CostItem>{t('cart.shared.estTax')}</CostItem>
        <CostItem>
          {orderCost?.currency.symbol}
          {customTax ? customTax.toFixed(2) : orderCost?.tax}
        </CostItem>
      </Row>
      <Row>
        <CostItem>{t('cart.shared.shipping')}</CostItem>
        <CostItem>
          <DisplayValue
            displayStatus={
              props.deliveryCostError && props.orderType === 'delivery'
                ? 'warning'
                : undefined
            }
          >
            {orderCost?.currency.symbol}
            {props.deliveryCostError && props.orderType === 'delivery'
              ? '--'
              : shippingCost}
          </DisplayValue>
        </CostItem>
      </Row>
      <Row>
        <CostItem>
          {t('cart.shared.tip')}
          <HelpIcon
            data-tooltip-id="tipDetails"
            data-tooltip-content={t('cart.shared.service.tooltip.tipTootipText')}
          >
            <FontAwesomeIcon icon={faQuestionCircle} />
          </HelpIcon>
        </CostItem>
        <CostItem>
          {orderCost?.currency.symbol}
          {customTip ? customTip.toFixed(2) : orderCost?.tip}
        </CostItem>
      </Row>
      <Tooltip
        id="tipDetails"
        place="top"
        variant="dark"
        openOnClick
        globalCloseEvents={{ clickOutsideAnchor: true }}
        className="extraClass"
      />
      <Row>
        <TotalItem>{t('cart.shared.total')}</TotalItem>
        <TotalItem>
          {orderCost?.currency.symbol}
          {Number(total).toFixed(2)}
        </TotalItem>
      </Row>
    </Wrapper>
  );
};
